/**
 * The shape of a TrustCare quality form, as served by `GET /forms/:name`.
 *
 * The web deliberately carries no copy of these forms. It draws whatever the
 * API sends and the API validates against the same model, so the questions on
 * screen and the questions the register accepts cannot drift apart — there is
 * one definition, in one place, and this file only describes its shape.
 */

export interface FormOption {
    value: string;
    label: string;
}

export interface ShowRule {
    field: string;
    test: "equals" | "notEquals" | "answered" | "notAnswered" | "isTrue" | "isFalse";
    value?: string;
}

/** One `and` term. Its rules are alternatives, so any one of them satisfies it. */
export interface ShowClause {
    anyOf: ShowRule[];
}

export interface FormField {
    key: string;
    type: string;
    label: string;
    page: string;
    required: boolean;
    hidden: boolean;
    options?: FormOption[];
    description?: string;
    showWhen: ShowClause[];
}

export interface FormPage {
    title: string;
    fields: string[];
}

export interface FormModel {
    name: string;
    title: string;
    pages: FormPage[];
    fields: Record<string, FormField>;
}

export type Answers = Record<string, unknown>;

/** Whether a question has been answered at all — "" and an empty tick-list are not answers. */
export const isAnswered = (value: unknown): boolean => {
    if (value === undefined || value === null || value === "") return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.values(value as object).some(isAnswered);
    return true;
};

const holds = (rule: ShowRule, answers: Answers): boolean => {
    const value = answers[rule.field];
    switch (rule.test) {
        case "equals": return String(value ?? "") === String(rule.value ?? "");
        case "notEquals": return String(value ?? "") !== String(rule.value ?? "");
        case "answered": return isAnswered(value);
        case "notAnswered": return !isAnswered(value);
        case "isTrue": return value === true || value === "true";
        case "isFalse": return value === false || value === "false";
    }
};

/**
 * Whether this question applies, given what has been answered so far.
 *
 * Mirrors the API exactly: every clause must hold, and a clause holds when any
 * of its alternatives does. A question with no rules is always shown.
 */
export const isVisible = (field: FormField, answers: Answers): boolean =>
    !field.hidden && field.showWhen.every((clause) => clause.anyOf.some((rule) => holds(rule, answers)));

/** The pages that still have something to ask, in the form's own order. */
export const visiblePages = (model: FormModel, answers: Answers): Array<{ title: string; fields: FormField[] }> =>
    model.pages
        .map((page) => ({
            title: page.title,
            fields: page.fields
                .map((key) => model.fields[key])
                .filter((f): f is FormField => Boolean(f) && isVisible(f, answers)),
        }))
        .filter((page) => page.fields.length > 0);

/** Required questions on this page that nobody has answered yet. */
export const unanswered = (fields: FormField[], answers: Answers): FormField[] =>
    fields.filter((f) => f.required && !isAnswered(answers[f.key]));

/**
 * Answers to questions the current answers would no longer show.
 *
 * They are removed before filing rather than as the reporter types: changing an
 * incident from a fall to a medication error and back must not silently wipe
 * what was already written about the fall. The API refuses answers to hidden
 * questions, so they cannot be sent either way.
 */
export const applicableAnswers = (model: FormModel, answers: Answers): Answers => {
    const kept: Answers = {};
    for (const [key, value] of Object.entries(answers)) {
        const field = model.fields[key];
        if (!field || !isVisible(field, answers) || !isAnswered(value)) continue;
        kept[key] = value;
    }
    return kept;
};
